/* eslint max-lines: ["error", {"max": 400, "skipComments": true}] */

import {
  CLEAR_QUERY_PARAMS,
  FOLLOW_ADVICE,
  FORCE_TYPO,
  RESET_FILTERS,
  RESET_FROM_QUERY_PASTE,
  RESTRICT_TO_ENTERPRISE,
  RESTRICT_TO_LAWYER,
  SELECT_KEYWORD,
  SHOW_ALL_RESULTS,
  SYNC_URL_PARAMS,
  TOGGLE_AGGREGATION,
  TOGGLE_EXCLUDE_MOYENS_ANNEXES,
  UNSELECT_KEYWORD,
  UPDATE_DATES,
  REMOVE_RESTRICTION_TO_LAW_ARTICLE_ID,
  ADD_RESTRICTION_TO_LAW_ARTICLE_ID,
  ADD_RESTRICTION_TO_LAW_ARTICLE_IDS,
  UPDATE_PARTIES,
  UPDATE_LAWYER_FILTER,
  UPDATE_ENTERPRISE_FILTER,
  UPDATE_LAW_OFFICE_FILTER,
  UPDATE_QUERY,
  UPDATE_SORT,
  UPDATE_TYPE,
  USER_TYPE,
  USER_ENHANCED_QUERY_BY_PASTING,
  MANUAL_SUBMIT_SEARCH,
} from '../action-types';
import { convertUrlParamsToSearchState } from '../utils/convertUrlParamsToSearchState';
import { SortTypes, SearchTypes, AdviceType } from '../../search.types';
import { StateType } from '../../../state/state.types';

export const updateQuery = (query: string) =>
  ({
    type: UPDATE_QUERY,
    payload: { query },
  } as const);

export const userType = (query: string) =>
  <const>{
    type: USER_TYPE,
    payload: { query },
  };

export const userEnhancedQueryByPasting = (query: string) =>
  <const>{
    type: USER_ENHANCED_QUERY_BY_PASTING,
    payload: {
      query,
    },
  };

export const updateSort = (sort: SortTypes) =>
  <const>{
    type: UPDATE_SORT,
    payload: { sort },
  };

export const toggleAggregation = (
  aggregationPath: Array<string>,
  value: boolean,
) =>
  <const>{
    type: TOGGLE_AGGREGATION,
    payload: { aggregationPath, value },
  };

export const toggleExcludeMoyensAnnexes = () =>
  <const>{
    type: TOGGLE_EXCLUDE_MOYENS_ANNEXES,
  };

export const updateDates = (dates: {
  from?: string;
  to?: string;
  exact?: string;
}) =>
  <const>{
    type: UPDATE_DATES,
    payload: { dates },
  };

export const updateParties = (parties: {
  plaintiff?:
    | {
        name?: string | undefined;
        type?: string | undefined;
      }
    | null
    | undefined;
  defendant?:
    | {
        name?: string | undefined;
        type?: string | undefined;
      }
    | null
    | undefined;
}) =>
  <const>{
    type: UPDATE_PARTIES,
    payload: { parties },
  };

export const updateLawyerFilter = (lawyerId: string | undefined) =>
  <const>{
    type: UPDATE_LAWYER_FILTER,
    payload: { lawyerId },
  };

export const updateEnterpriseFilter = (enterpriseId: string | undefined) =>
  <const>{
    type: UPDATE_ENTERPRISE_FILTER,
    payload: { enterpriseId },
  };

export const updateLawOfficeFilter = (lawOfficeId: string | undefined) =>
  <const>{
    type: UPDATE_LAW_OFFICE_FILTER,
    payload: { lawOfficeId },
  };

export const updateType = (type: SearchTypes) =>
  <const>{
    type: UPDATE_TYPE,
    payload: { type },
  };

const followAdviceAction = (advice: AdviceType) =>
  <const>{
    type: FOLLOW_ADVICE,
    payload: { advice },
  };

export const followAdvice = () => (
  dispatch: Function,
  getState: () => StateType,
) => {
  const state = getState();
  if (state.results.adviceAction) {
    dispatch(followAdviceAction(state.results.adviceAction));
  }
};

export const forceTypo = () =>
  <const>{
    type: FORCE_TYPO,
  };

export const manualSubmitSearch = () =>
  <const>{
    type: MANUAL_SUBMIT_SEARCH,
  };

export const resetFilters = () =>
  <const>{
    type: RESET_FILTERS,
  };

export const resetFromQueryPaste = (query: string) =>
  <const>{
    type: RESET_FROM_QUERY_PASTE,
    payload: {
      query,
    },
  };

const showAllResultsAction = (hitsDisplayedCount: number) =>
  <const>{
    type: SHOW_ALL_RESULTS,
    payload: {
      hitsDisplayedCount,
    },
  };

export const showAllResults = () => (
  dispatch: Function,
  getState: () => StateType,
) => {
  const state = getState();
  dispatch(showAllResultsAction(state.results.hits.length));
};

export const selectKeyword = (keyword: string) =>
  <const>{
    type: SELECT_KEYWORD,
    payload: {
      keyword,
    },
  };

export const unSelectKeyword = (keyword: string) =>
  <const>{
    type: UNSELECT_KEYWORD,
    payload: {
      keyword,
    },
  };

export const addRestrictionToLawArticleIds = (lawArticleIds: string[]) =>
  <const>{
    type: ADD_RESTRICTION_TO_LAW_ARTICLE_IDS,
    payload: {
      lawArticleIds,
    },
  };

export const addRestrictionToLawArticleId = (lawArticleId: string) =>
  <const>{
    type: ADD_RESTRICTION_TO_LAW_ARTICLE_ID,
    payload: {
      lawArticleId,
    },
  };

export const removeRestrictionToLawArticleId = (lawArticleId: string) =>
  <const>{
    type: REMOVE_RESTRICTION_TO_LAW_ARTICLE_ID,
    payload: {
      lawArticleId,
    },
  };

export const restrictToLawyer = ({
  lawyerId,
  lawyerName,
  juridiction,
  ville,
}: {
  lawyerId: string;
  lawyerName: string;
  juridiction?: string;
  ville?: string;
}) =>
  <const>{
    type: RESTRICT_TO_LAWYER,
    payload: {
      lawyerId,
      lawyerName,
      juridiction,
      ville,
    },
  };

export const restrictToEnterprise = ({
  enterpriseId,
  raisonSociale,
  juridiction,
  ville,
  year,
}: {
  enterpriseId: string;
  raisonSociale: string;
  ville?: string | null;
  juridiction?: string | null;
  year?: string;
}) =>
  <const>{
    type: RESTRICT_TO_ENTERPRISE,
    payload: { enterpriseId, raisonSociale, juridiction, ville, year },
  };

export const clearQueryParams = () =>
  <const>{
    type: CLEAR_QUERY_PARAMS,
  };

export const syncUrlParams = (url: string) =>
  <const>{
    type: SYNC_URL_PARAMS,
    payload: {
      params: convertUrlParamsToSearchState(url),
    },
  };

export type QueryParamsActions = ReturnType<
  | typeof updateQuery
  | typeof userType
  | typeof userEnhancedQueryByPasting
  | typeof updateSort
  | typeof toggleAggregation
  | typeof toggleExcludeMoyensAnnexes
  | typeof updateDates
  | typeof updateParties
  | typeof updateLawyerFilter
  | typeof updateEnterpriseFilter
  | typeof updateType
  | typeof followAdviceAction
  | typeof forceTypo
  | typeof manualSubmitSearch
  | typeof resetFilters
  | typeof resetFromQueryPaste
  | typeof showAllResultsAction
  | typeof selectKeyword
  | typeof unSelectKeyword
  | typeof addRestrictionToLawArticleIds
  | typeof addRestrictionToLawArticleId
  | typeof removeRestrictionToLawArticleId
  | typeof clearQueryParams
  | typeof syncUrlParams
  | typeof restrictToLawyer
  | typeof restrictToEnterprise
  | typeof updateLawOfficeFilter
>;
